import React, { useState } from 'react';
import { reduxForm, Form, InjectedFormProps, FieldArray } from 'redux-form';
import { compose } from 'ramda';
import { makeStyles, Grid } from '@material-ui/core';
import { ExistingPolicyType } from 'store/modules/ExistingPolicies/types';
import { ContributionSummaryType } from 'store/modules/Contributions/types';
import Forms from 'components/Forms';
import { Button, BoxWrapper, Modal } from 'components/Base';
import Store from 'store/store';
import api from 'api';
import Router from 'components/Router/Router';
import { Notify, formatCurrency } from 'utils';

const useStyles = makeStyles({
  spaceContainer: {
    marginBottom: '1.5rem',
    marginLeft: '1rem'
  },
  btnText: {
    display: 'flex',
    flexDirection: 'column'
  },
  btnAddPolicy: {
    marginTop: '1rem'
  },
  btnEvaluate: {
    marginTop: '1rem',
    marginRight: '1rem'
  },
  paper: {
    backgroundColor: '#fff',
    padding: '2rem 1rem'
  }
});

const { useParams } = Router;
const { connect, actions, selectors } = Store;

interface RecommendationsProps {
  resetCase: any;
  getRecommendationsData: any;
  hasExistingPolicies: boolean;
  existingPolicies: Array<ExistingPolicyType>;
  recommendedPolicies: any;
  contributionSummary: ContributionSummaryType;
}
const Recommendations = ({
  handleSubmit,
  getRecommendationsData,
  hasExistingPolicies,
  existingPolicies,
  recommendedPolicies,
  contributionSummary
}: InjectedFormProps & RecommendationsProps) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [evaluateLoading, setEvaluateLoading] = useState(false);
  const { portfolio } = useParams() as any;
  const allActionsRun = existingPolicies.every(
    policy => policy.performed_action !== null
  );
  const { unallocated_existing } = contributionSummary;

  const hasPoliciesWithoutAllocations = recommendedPolicies.some(
    (policy: any) => policy.fund_allocations.length === 0
  );

  const evaluate = () => {
    setIsPromptOpen(false);
    setEvaluateLoading(true);
    api
      .evaluate({ portfolio })
      .then(() => {
        getRecommendationsData({ portfolio }).then(() => {
          setEvaluateLoading(false);
          setIsLoading(false);
        });
      })
      .catch((err: any) => {
        setEvaluateLoading(false);
        Notify.error(err.response.data.message);
      });
  };

  return (
    <>
      <Grid container>
        <Form
          onSubmit={handleSubmit}
          style={{ width: '100%', marginTop: '2rem' }}
        >
          <Grid container spacing={2}>
            {hasExistingPolicies && (
              <Grid item xs={12}>
                <BoxWrapper title="Existing portfolio">
                  <FieldArray
                    name="policies"
                    component={Forms.Policies}
                    rerenderOnEveryChange
                  />
                </BoxWrapper>
              </Grid>
            )}
            <Grid item xs={12}>
              <FieldArray
                name="contributions"
                component={Forms.Contributions}
                hasExistingPolicies={hasExistingPolicies}
                rerenderOnEveryChange
              />
            </Grid>
            <Grid item xs={12}>
              <FieldArray
                name="withdrawals"
                component={Forms.Withdrawals}
                rerenderOnEveryChange
              />
            </Grid>
            <Grid item xs={12}>
              <FieldArray
                name="recommendedPolicies"
                component={Forms.RecommendedPolicies}
                rerenderOnEveryChange
              />
            </Grid>
            <Grid item xs={12} className={classes.spaceContainer}>
              <Button
                type="button"
                onClick={() => {
                  unallocated_existing > 0 ||
                  !allActionsRun ||
                  hasPoliciesWithoutAllocations
                    ? setIsPromptOpen(true)
                    : evaluate();
                }}
                isLoading={evaluateLoading}
              >
                Evaluate
              </Button>
              <Button
                id="reset-recommendations"
                color="primary"
                type="button"
                isLoading={isLoading}
                style={{ marginLeft: '1rem' }}
                //TODO remove when we will have full flow
                onClick={() => {
                  setIsLoading(true);
                  return api
                    .resetCase({ portfolio })
                    .then(() => {
                      getRecommendationsData({ portfolio }).then(() =>
                        setIsLoading(false)
                      );
                    })
                    .catch((err: any) => {
                      Notify.error(err.response.data.message);
                      setIsLoading(false);
                    });
                }}
              >
                Reset Recommendation
              </Button>
            </Grid>
          </Grid>
        </Form>
      </Grid>
      <Modal
        isOpen={isPromptOpen}
        closeModalFunc={() => setIsPromptOpen(false)}
      >
        <BoxWrapper title="Action Required">
          <div style={{ padding: '1rem' }}>
            <ul>
              {!allActionsRun && (
                <li>Not all policies have actions run against them.</li>
              )}
              {unallocated_existing > 0 && (
                <li>
                  There is {formatCurrency(unallocated_existing)} of unallocated
                  existing money.
                </li>
              )}
              {recommendedPolicies.map(
                (policy: any) =>
                  policy.fund_allocations.length === 0 && (
                    <li key={policy.id || policy.display_name}>
                      {policy.display_name} has no fund allocations.
                    </li>
                  )
              )}
            </ul>
          </div>

          <div
            style={{
              display: 'flex',
              minWidth: '25rem',
              maxWidth: '100%',
              justifyContent: 'space-between',
              padding: '1rem'
            }}
          >
            <Button
              onClick={() => setIsPromptOpen(false)}
              color="primary"
              type="button"
            >
              Cancel
            </Button>
          </div>
        </BoxWrapper>
      </Modal>
    </>
  );
};
export default compose(
  connect(
    (state: any) => ({
      existingPolicies: selectors.caseSelectors.existingPolicies(state),
      recommendedPolicies: selectors.caseSelectors.policies(state),
      contributionSummary: selectors.caseSelectors.contributionSummary(state),
      hasExistingPolicies: selectors.caseSelectors.hasExistingPolicies(state)
    }),
    {
      getRecommendationsData:
        actions.recommendationActions.getRecommendationData
    }
  ),
  reduxForm({
    form: 'recommendations',
    enableReinitialize: true
  })
)(Recommendations);
